import type { AwardSubCriterionDto, TemplateContentDto } from "../../autogen/bff-api";
import { templateDefinesSection } from "./applyTemplate";
import { ALL_SECTIONS, type TemplateSection } from "./templateFromEvent";

export interface TemplateSelection {
  sections: TemplateSection[];
  minimum: number[];
  selection: number[];
  award: number[];
  awardSub: Record<number, number[]>;
}

export const EMPTY_SELECTION: TemplateSelection = {
  sections: [],
  minimum: [],
  selection: [],
  award: [],
  awardSub: {},
};

const indices = (length: number): number[] => Array.from({ length }, (_, i) => i);

export const sectionsDefinedBy = (content: TemplateContentDto): TemplateSection[] =>
  ALL_SECTIONS.filter((section) => templateDefinesSection(content, section));

export const awardCriteriaOf = (content: TemplateContentDto) =>
  content.awardCriteria?.bestPriceQualityRatio?.criteria ?? [];

export const rowCount = (content: TemplateContentDto, section: TemplateSection): number => {
  switch (section) {
    case "minimum":
      return content.minimumRequirements?.length ?? 0;
    case "selection":
      return content.selectionCriteria?.length ?? 0;
    case "award":
      return awardCriteriaOf(content).length;
  }
};

export const chosenRows = (selection: TemplateSelection, section: TemplateSection): number[] =>
  section === "minimum" ? selection.minimum : section === "selection" ? selection.selection : selection.award;

export const withSectionRows = (
  selection: TemplateSelection,
  section: TemplateSection,
  rows: number[]
): TemplateSelection =>
  section === "minimum"
    ? { ...selection, minimum: rows }
    : section === "selection"
      ? { ...selection, selection: rows }
      : { ...selection, award: rows };

export const selectWholeSections = (
  content: TemplateContentDto,
  sections: TemplateSection[]
): TemplateSelection => {
  const wanted = sections.filter((section) => templateDefinesSection(content, section));
  return {
    sections: wanted,
    minimum: wanted.includes("minimum") ? indices(rowCount(content, "minimum")) : [],
    selection: wanted.includes("selection") ? indices(rowCount(content, "selection")) : [],
    award: wanted.includes("award") ? indices(rowCount(content, "award")) : [],
    awardSub: wanted.includes("award")
      ? Object.fromEntries(awardCriteriaOf(content).map((c, i) => [i, indices(c.subCriteria?.length ?? 0)]))
      : {},
  };
};

export const toggleSection = (
  content: TemplateContentDto,
  selection: TemplateSelection,
  section: TemplateSection
): TemplateSelection => {
  if (selection.sections.includes(section)) {
    const cleared = withSectionRows({ ...selection, sections: selection.sections.filter((s) => s !== section) }, section, []);
    return section === "award" ? { ...cleared, awardSub: {} } : cleared;
  }
  const whole = selectWholeSections(content, [section]);
  const added = withSectionRows(
    { ...selection, sections: [...selection.sections, section] },
    section,
    chosenRows(whole, section)
  );
  return section === "award" ? { ...added, awardSub: whole.awardSub } : added;
};

const withSection = (selection: TemplateSelection, section: TemplateSection, on: boolean): TemplateSelection => ({
  ...selection,
  sections: on
    ? selection.sections.includes(section)
      ? selection.sections
      : [...selection.sections, section]
    : selection.sections.filter((s) => s !== section),
});

export const toggleRow = (
  selection: TemplateSelection,
  section: TemplateSection,
  index: number
): TemplateSelection => {
  const rows = chosenRows(selection, section);
  const next = rows.includes(index) ? rows.filter((i) => i !== index) : [...rows, index];
  return withSectionRows(withSection(selection, section, next.length > 0), section, next);
};

export const toggleAwardSubRow = (
  selection: TemplateSelection,
  criterionIndex: number,
  subIndex: number
): TemplateSelection => {
  const subs = selection.awardSub[criterionIndex] ?? [];
  const nextSubs = subs.includes(subIndex) ? subs.filter((i) => i !== subIndex) : [...subs, subIndex];
  const withSub = { ...selection, awardSub: { ...selection.awardSub, [criterionIndex]: nextSubs } };
  if (nextSubs.length === 0 || selection.award.includes(criterionIndex)) return withSub;
  return toggleRow(withSub, "award", criterionIndex);
};

export const everythingSelected = (content: TemplateContentDto, selection: TemplateSelection): boolean => {
  const defined = sectionsDefinedBy(content);
  return (
    defined.length > 0 &&
    defined.every(
      (section) =>
        selection.sections.includes(section) && chosenRows(selection, section).length === rowCount(content, section)
    )
  );
};

export const toggleEverything = (
  content: TemplateContentDto,
  selection: TemplateSelection
): TemplateSelection =>
  everythingSelected(content, selection) ? EMPTY_SELECTION : selectWholeSections(content, sectionsDefinedBy(content));

// The BFF's SUB_CRITERION_WEIGHTS_MUST_SUM_TO_100 gate requires each set to total exactly 100.
export const adjustedSubWeights = (
  subCriteria: AwardSubCriterionDto[],
  chosen: number[]
): Record<number, number> => {
  const picked = [...chosen].sort((a, b) => a - b);
  if (picked.length === 0 || picked.length === subCriteria.length) return {};

  const weights = picked.map((i) => subCriteria[i]?.weight?.number);
  if (weights.some((w) => w == null)) return {};
  const total = weights.reduce((sum: number, w) => sum + (w ?? 0), 0);
  if (total <= 0) return {};

  const scaled = weights.map((w) => Math.floor(((w ?? 0) * 100) / total));
  scaled[0] += 100 - scaled.reduce((a, b) => a + b, 0);

  return Object.fromEntries(picked.map((i, n) => [i, scaled[n]]));
};

export const chosenWeightTotal = (content: TemplateContentDto, selection: TemplateSelection): number =>
  awardCriteriaOf(content)
    .filter((_, i) => selection.award.includes(i))
    .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);

export const contributingSections = (
  content: TemplateContentDto,
  selection: TemplateSelection
): TemplateSection[] =>
  selection.sections.filter((section) => {
    if (section === "award" && awardCriteriaOf(content).length === 0) return true;
    return chosenRows(selection, section).length > 0;
  });

export const pickFromTemplate = (
  content: TemplateContentDto,
  selection: TemplateSelection
): TemplateContentDto => {
  const chosen = contributingSections(content, selection);
  const picked: TemplateContentDto = {};

  if (chosen.includes("minimum")) {
    picked.minimumRequirements = (content.minimumRequirements ?? []).filter((_, i) =>
      selection.minimum.includes(i)
    );
  }

  if (chosen.includes("selection")) {
    picked.selectionCriteria = (content.selectionCriteria ?? []).filter((_, i) =>
      selection.selection.includes(i)
    );
  }

  if (chosen.includes("award") && content.awardCriteria) {
    picked.awardCriteria = content.awardCriteria.bestPriceQualityRatio
      ? {
          lowestPrice: null,
          bestPriceQualityRatio: {
            criteria: awardCriteriaOf(content)
              .map((c, i) => {
                const chosenSubs = selection.awardSub[i] ?? [];
                const adjusted = adjustedSubWeights(c.subCriteria ?? [], chosenSubs);
                return {
                  ...c,
                  subCriteria: (c.subCriteria ?? []).flatMap((sub, s) => {
                    if (!chosenSubs.includes(s)) return [];
                    const weight = adjusted[s];
                    return [
                      weight == null || sub.weight == null ? sub : { ...sub, weight: { ...sub.weight, number: weight } },
                    ];
                  }),
                };
              })
              .filter((_, i) => selection.award.includes(i)),
          },
        }
      : content.awardCriteria;
    picked.priceModel = content.priceModel;
  }

  return picked;
};
